'use client';

import { motion } from 'framer-motion';
import { useLocale } from 'next-intl';
import Image from 'next/image';
import { products, getProductCopy } from '@/data/products';

const translations = {
  en: {
    sectionTag: 'Our Products',
    title: 'We don\'t only build AI for clients — we ship our own',
    subtitle: 'The products below are ours end to end: our idea, our engineering, running in production today.',
    visit: 'Visit',
    learnMore: 'See all products',
  },
  lt: {
    sectionTag: 'Mūsų Produktai',
    title: 'Kuriame AI ne tik klientams — turime ir savo produktų',
    subtitle: 'Žemiau esantys produktai yra visiškai mūsų: mūsų idėja, mūsų inžinerija, jau veikianti realiai.',
    visit: 'Aplankyti',
    learnMore: 'Visi produktai',
  },
};

export default function ProductsSection() {
  const locale = useLocale();
  const t = translations[locale as keyof typeof translations] || translations.en;
  const isLithuanian = locale === 'lt';
  const productsHref = isLithuanian ? '/lt/produktai' : '/produktai';

  return (
    <section id="products" className="py-28 bg-gradient-to-b from-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background effects — continues the dark treatment from the services section above */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-violet-500/15 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:72px_72px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-3xl mb-14"
        >
          <span className="text-violet-400 font-semibold text-sm uppercase tracking-wider">
            {t.sectionTag}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-5 leading-tight">
            {t.title}
          </h2>
          <p className="text-xl text-gray-400 leading-relaxed">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Product cards */}
        <div className="space-y-8">
          {products.map((product, index) => {
            const copy = getProductCopy(product, locale);

            return (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden"
              >
                <div className="grid lg:grid-cols-2">
                  {/* Left — identity and pitch */}
                  <div className="p-8 sm:p-12">
                    {/* The wordmark is light-on-transparent, so it sits directly on
                        this dark surface. Products with no logo get a tinted tile. */}
                    <div className="mb-6">
                      {product.logo && product.logoSize ? (
                        <Image
                          src={product.logo}
                          alt={product.name}
                          width={product.logoSize.width}
                          height={product.logoSize.height}
                          className="h-10 w-auto"
                          // SVG is vector — the optimizer cannot improve it, and
                          // Next 14's content sniffing only recognises SVG from a
                          // leading `<?xml`, so optimising this file 400s.
                          unoptimized
                        />
                      ) : (
                        <span className={`inline-flex items-center justify-center rounded-2xl bg-gradient-to-br ${product.accent} px-5 py-3 text-xl font-bold tracking-tight text-white shadow-lg shadow-violet-500/25`}>
                          {product.name}
                        </span>
                      )}
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 leading-snug">
                      {copy.tagline}
                    </h3>
                    <p className="text-gray-400 text-lg leading-relaxed mb-8">
                      {copy.shortDescription}
                    </p>

                    {/* Highlights */}
                    <ul className="space-y-3 mb-9">
                      {copy.highlights.map((highlight) => (
                        <li key={highlight} className="flex items-start text-gray-300">
                          <svg className="w-5 h-5 text-violet-400 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex flex-wrap gap-4">
                      <motion.a
                        href={product.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ y: -2 }}
                        whileTap={{ y: 0 }}
                        className={`inline-flex items-center gap-2 rounded-xl bg-gradient-to-r ${product.accent} px-6 py-3.5 font-semibold text-white shadow-lg shadow-violet-500/25 transition-shadow hover:shadow-xl hover:shadow-violet-500/40`}
                      >
                        {t.visit} {product.name}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </motion.a>

                      <a
                        href={productsHref}
                        className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-6 py-3.5 font-semibold text-gray-200 transition-colors hover:bg-white/10 hover:text-white"
                      >
                        {t.learnMore}
                      </a>
                    </div>
                  </div>

                  {/* Right — capability grid */}
                  <div className="border-t border-white/10 lg:border-t-0 lg:border-l bg-white/[0.02] p-8 sm:p-12">
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-9">
                      {copy.features.map((feature) => (
                        <div key={feature.title}>
                          <div className="text-2xl mb-3" aria-hidden="true">{feature.icon}</div>
                          <h4 className="text-white font-semibold mb-2">{feature.title}</h4>
                          <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
